import React, { useCallback, useEffect, useState } from "react";
import styled from "styled-components";
import ReactHtmlParser from "react-html-parser";
import { useForm } from "react-hook-form";
import Layout from "../providers/Layout";
import { ArticleListBreadcrumbs } from "../components/Breadcrumbs/Breadcrumbs";
import Textarea from "../components/FormsElements/Textarea";
import TextInput from "../components/FormsElements/TextInput";
import BoxLegalAdvice from "../components/BoxLegalAdvice/BoxLegalAdvice";
import BoxBuySubscription from "../components/BoxBuySubscription/BoxBuySubscription";
import BoxNewsletter from "../components/BoxNewsletter/BoxNewsletter";
import MapGoogle from "../components/MapGoogle/MapGoogle";
import PermInfo from "../components/PermInfo/PermInfo";
import Button from "../components/Button/Button";
import Alert from "../components/Alert/Alert";

import ArticleAuthorsItem from "../components/ArticleAuthors/ArticleAuthorsItem";
import {
  getProfileTemplatePerms,
  postUserPerms,
} from "../services/requestsPerms";
import { getLayoutProps } from "../services/serverSideRequests";
import {
  sendContactForm,
  fetchApi,
  getJournalistsByContactCategory,
} from "../services/requests";
import { Page, MainContent, Aside, PageHeader } from "../services/styles";
import { validateEmail } from "../services/validation";
import { Flex } from "../styled/Styled.sc";

const ContactSection = styled.div`
  margin: 0 0 40px 0;
  border-bottom: 4px solid #f5f5f5;
`;

const SectionHeader = styled.p`
  font-size: 24px;
  line-height: 32px;
  font-weight: 900;
  margin: 0 0 20px 0;
`;

const Row = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
`;

const RowStart = styled(Row)`
  justify-content: flex-start;
  flex-wrap: wrap;
`;

const Form = styled.form`
  margin: 40px 0;
`;

const PermLabel = styled.div`
  font-size: 10px;
  line-height: 12px;
  margin-bottom: 10px;
`;

const Kontakt = ({
  navbarLinks,
  submenuLists,
  paths,
  mainCategories,
  importantArticles,
  topBlock,
  perms,
  contactCategory,
  journalists,
  formBlock,
}) => {
  const [alert, setAlert] = useState({
    type: "success",
    text: "",
    active: false,
  });
  const sections = contactCategory ? contactCategory.results : [];
  const { register, handleSubmit, errors, reset } = useForm();

  const [permValues, setPerms] = useState({});
  const setPerm = useCallback(
    (newPerm) => setPerms((p) => ({ ...p, ...newPerm })),
    [setPerms]
  );

  const submitForm = async (data) => {
    if (Object.keys(permValues).length > 0) {
      postUserPerms(permValues, data.email);
    }
    const [resp, err] = await sendContactForm({
      ...data,
      topic: 14,
    });
    if (!err) {
      setAlert({
        type: "success",
        text: "Wiadomość została wysłana.",
        active: true,
      });
      reset();
    } else {
      setAlert({
        type: "error",
        text: "Nie udało się wysłać wiadomości. Spróbuj ponownie.",
        active: true,
      });
    }
  };

  const getEmailErrorText = (error) => {
    if (error && error.type === "validate") {
      return { ...error, message: "Niepoprawny adres email" };
    } else {
      return error;
    }
  };

  const permData = Array.isArray(perms) && perms.length ? perms[0] : null;
  return (
    <Layout
      navbarLinks={navbarLinks}
      paths={paths}
      submenuLists={submenuLists}
      mainCategories={mainCategories}
      importantArticles={importantArticles}
      metatagsData={{ title: "Kontakt - portal agrarlex.pl" }}
    >
      <Page>
        <Aside>
          <BoxLegalAdvice />
          <BoxBuySubscription />
          <BoxNewsletter />
        </Aside>
        <MainContent>
          <ArticleListBreadcrumbs article={"Kontakt"} />
          <PageHeader>Kontakt</PageHeader>
          {topBlock && (
            <div className="wysiwyg">{ReactHtmlParser(topBlock.content)}</div>
          )}
          {sections.map((section) => (
            <ContactSection key={`contactSection${section.pk}`}>
              <SectionHeader>{section.name}</SectionHeader>
              <div className="wysiwyg">
                {ReactHtmlParser(section.description)}
              </div>
              <RowStart>
                {section.journalists.map((journalistId) => {
                  const data = journalists.find((el) => el.pk === journalistId);
                  if (!data) return null;
                  return (
                    <ArticleAuthorsItem
                      authors={data}
                      key={`journalist${data.pk}`}
                      contactData={true}
                    />
                  );
                })}
              </RowStart>
            </ContactSection>
          ))}
          <SectionHeader>Formularz kontaktowy</SectionHeader>
          {formBlock && (
            <div className="wysiwyg">{ReactHtmlParser(formBlock.content)}</div>
          )}
          <Alert message={alert} setMessage={setAlert} />
          <ContactSection>
            <SectionHeader>Lokalizacja</SectionHeader>
            <MapGoogle />
          </ContactSection>
        </MainContent>
      </Page>
    </Layout>
  );
};

export async function getServerSideProps(context) {
  const layoutProps = await getLayoutProps();
  const [topBlock] = await fetchApi({ url: `blocks-block/334/` });
  const [formBlock] = await fetchApi({ url: `blocks-block/335/` });
  const [contactCategory] = await fetchApi({
    url: `contact-category/?site=12`,
  });
  const journalists = contactCategory
    ? await getJournalistsByContactCategory(contactCategory.results)
    : [];
  const [perms] = await getProfileTemplatePerms("contact-agrarlex");
  return {
    props: {
      ...layoutProps,
      topBlock,
      perms,
      contactCategory,
      journalists,
      formBlock,
    },
  };
}

export default Kontakt;

const PermContainer = ({ perms = [], setPerm, values }) => {
  function handleCheckbox(e) {
    const target = e.target;
    setPerm({ [target.name]: target.checked });
  }

  return perms.map((perm, index) => {
    if (perm.permtype === "checkbox")
      return (
        <div key={index}>
          <Flex>
            <input
              style={{ marginBottom: 10, marginTop: 0 }}
              type="checkbox"
              name={perm.type}
              onChange={handleCheckbox}
              checked={Boolean(values[perm.type]) === true}
            />
            <PermLabel>{perm.short}</PermLabel>
          </Flex>
          <PermInfo short={""} extended={perm.extended || perm.full} />
        </div>
      );
    return (
      <div key={index}>
        <PermInfo short={perm.short} extended={perm.extended || perm.full} />
      </div>
    );
  });
};
